import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import * as CompareActions from '../../store/compare/compareActions';
import * as CompareTrayActions from 'connected-components/store/compareTray/compareTrayActions';
import * as PriceActions from 'connected-components/store/price/priceActions';
import * as AvailabilityActions from 'connected-components/store/availability/availabilityActions';
import CompareView from '../../components/CompareView';
import lodashGet from 'lodash.get';
import lodashDiff from 'lodash.difference';
import MetaData from '@ow/ow-components/lib/components/MetaData';
import { CANONICAL_HOST, COMPARE_PATH } from '../../../config/global.config';

const toObject = nameValueArray =>
  nameValueArray.reduce((acc, item) => ({ ...acc, [item.name]: item.value }), {});

class Compare extends Component {
  constructor(props) {
    super(props);

    // compare doesn't need price or availability, but add-to-cart does
    this.loadProductPrice();
    this.loadAvailability();
  }

  componentDidMount() {
    this.fetchProductsIfNeeded();
  }

  componentWillUnmount() {
    clearTimeout(this.swipeInstructionTimeout);
  }

  componentDidUpdate(prevProps) {
    this.fetchProductsIfNeeded();
    this.reloadAvailability(prevProps);
    this.reloadProductPrice(prevProps);
  }

  loadProductPrice() {
    const { compareTray, fetchProductPrice, isLoggedIn } = this.props;
    const skus = lodashGet(compareTray, 'products', []).map(product => product.sku);
    if (skus.length > 0 && isLoggedIn !== undefined) {
      fetchProductPrice(skus);
    }
  }

  reloadProductPrice(prevProps) {
    const { compareTray, isLoggedIn } = this.props;
    const skus = lodashGet(compareTray, 'products', []).map(product => product.sku);
    const prevSkus = lodashGet(prevProps.compareTray, 'products', []).map(product => product.sku);
    // check diff rather than using lodashEqual, because removing product from compare should not reload price
    if (
      (lodashDiff(skus, prevSkus).length > 0 && skus.length > 0) ||
      isLoggedIn !== prevProps.isLoggedIn
    ) {
      this.loadProductPrice();
    }
  }

  loadAvailability() {
    const { customer, compareTray, fetchProductAvailabilitySummary, isLoggedIn } = this.props;
    const myStoreId = lodashGet(customer, 'myStoreId', null);
    const deliveryPostcode = lodashGet(customer, 'deliveryPostcode', null);

    const productSummaries = lodashGet(compareTray, 'products', []).map(product => {
      return {
        sku: product.sku,
        productType: product.productType,
        rangedOnline: product.rangedOnline,
        rangedRetail: product.rangedRetail
      };
    });
    if (
      myStoreId &&
      deliveryPostcode &&
      productSummaries &&
      productSummaries.length > 0 &&
      isLoggedIn !== undefined
    ) {
      fetchProductAvailabilitySummary({
        skus: productSummaries.map(product => product.sku),
        storeId: myStoreId,
        postcode: deliveryPostcode
      });
    }
  }

  reloadAvailability(prevProps) {
    const { customer, compareTray, isLoggedIn } = this.props;
    const prevCompareTray = prevProps.compareTray;
    const skus = compareTray.products.map(product => product.sku);
    const prevSkus = prevCompareTray.products.map(product => product.sku);
    const myStoreId = lodashGet(customer, 'myStoreId', null);
    const deliveryPostcode = lodashGet(customer, 'deliveryPostcode', null);
    const prevMyStoreId = lodashGet(prevProps.customer, 'myStoreId', null);
    const prevDeliveryPostcode = lodashGet(prevProps.customer, 'deliveryPostcode', null);

    if (
      (lodashDiff(skus, prevSkus).length > 0 && skus.length > 0) ||
      myStoreId !== prevMyStoreId ||
      deliveryPostcode !== prevDeliveryPostcode ||
      isLoggedIn !== prevProps.isLoggedIn
    ) {
      this.loadAvailability();
    }
  }

  fetchProductsIfNeeded() {
    const skusToFetch = this.props.compareTray.products
      .filter(product => !this.props.compareProductsInfo[product.sku])
      .map(product => product.sku);
    if (skusToFetch.length > 0) {
      this.props.getProducts(skusToFetch);
    }
  }

  render() {
    const {
      compareTray,
      compareProductsInfo,
      getProducts,
      removeFromCompare,
      setTrayItems,
      isLoggedIn
    } = this.props;

    const combinedSpecs = compareTray.products.reduce((specs, trayItem) => {
      const product = compareProductsInfo[trayItem.sku];
      if (!product || product.isFetching || !product.specs) {
        return specs;
      }
      const productGroups = product.specs;
      return productGroups.reduce((specs, spec) => {
        return { ...specs, [spec.group]: { ...specs[spec.group], ...toObject(spec.attributes) } };
      }, specs);
    }, {});

    return (
      <>
        <MetaData canonicalUrl={`${CANONICAL_HOST}${COMPARE_PATH}`} />
        <CompareView
          allSpecs={combinedSpecs}
          compareTray={compareTray}
          compareProductsInfo={compareProductsInfo}
          getProducts={getProducts}
          removeFromCompare={removeFromCompare}
          setTrayItems={setTrayItems}
          isLoggedIn={isLoggedIn}
        />
      </>
    );
  }
}

Compare.propTypes = {
  compareTray: PropTypes.object,
  compareProductsInfo: PropTypes.object,
  getProducts: PropTypes.func,
  removeFromCompare: PropTypes.func,
  setTrayItems: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  customer: PropTypes.object,
  fetchProductAvailabilitySummary: PropTypes.func,
  fetchProductPrice: PropTypes.func
};

Compare.defaultProps = {};

const mapStateToProps = ({ compareTray, owCompare, user, customer }) => ({
  compareTray,
  compareProductsInfo: owCompare.products,
  isLoggedIn: user.isLoggedIn,
  customer
});

const mapDispatchToProps = dispatch => ({
  getProducts: skus => dispatch(CompareActions.getProducts(skus)),
  setTrayItems: items => dispatch(CompareTrayActions.setCompareItems(items)),
  removeFromCompare: sku => dispatch(CompareTrayActions.removeFromCompare(sku)),
  fetchProductAvailabilitySummary: ({ skus, storeId, postcode }) => {
    dispatch(AvailabilityActions.getProductAvailabilitiesSummary({ skus, storeId, postcode }));
  },
  fetchProductPrice: skus => dispatch(PriceActions.getProductPrice(skus))
});

export default connect(mapStateToProps, mapDispatchToProps)(Compare);
export { Compare };
